/* SpawnAnything — capture zones on the client: "Siegbedingung" section in the setup panel, zone placement by click,
   rings on the ground (following the terrain), flag poles with voxel flags that rise with the progress and take the
   team colour, and the points strip in the HUD. The rules live in the simulation (src/sim/features/zone.js); this
   module only builds the `zones` field of the setup message and draws the worker's `{ type: 'zone' }` messages. */
import * as THREE from 'three';
import { TEAMS } from '../game/voxel.js';
import { isWalkable } from '../world/terrain.js';
import { ZONE_DEFAULTS, ZONE_MAX } from '../sim/features/zone.js';
import './zone.css';

const MODES = [
  ['vernichtung', 'Vernichtung', 'Gewonnen hat, wer alle Gegner besiegt.'],
  ['koenig', 'König des Hügels', 'Eine Zone in der Mitte, auf dem höchsten Punkt. Wer sie hält, bekommt jede Sekunde einen Punkt.'],
  ['herrschaft', 'Herrschaft', 'Drei Zonen quer über die Front. Jede gehaltene Zone bringt jede Sekunde einen Punkt.'],
];
const TARGET_RANGE = { koenig: [20, 300, 10], herrschaft: [50, 600, 25] };
const NEUTRAL = 0xf2efe6, CONTEST = 0xffc93c;
const MAST_H = 7.5, FW = 7, FH = 4, VS = 0.34; // flag: FW x FH voxels of VS metres
const LETTERS = 'ABCDE';
const nf = new Intl.NumberFormat('de-DE');
const fmt = n => nf.format(Math.floor(n));

/**
 * @param {{ scene: THREE.Scene, toonGrad: THREE.Texture, gen: () => number, simState: () => string,
 *           terrain: () => object|null, groundH: (x: number, z: number) => number,
 *           groundAt: (cx: number, cy: number, out: THREE.Vector3) => THREE.Vector3|false|null,
 *           toast: (msg: string) => void, resetup: () => void }} o
 * @returns {{ msg: () => object, pointer: (e: PointerEvent) => boolean, frame: (now: number) => void,
 *             victoryText: (winner: number) => string|null }}
 */
export function createZones(o) {
  const { scene, toonGrad, gen, simState, terrain, groundH, groundAt, toast, resetup } = o;
  let mode = 'vernichtung', custom = null, placing = false, last = null, prevO = [], dp = [], tPrev = 0, hudT = 0;
  const targets = { koenig: ZONE_DEFAULTS.koenig.target, herrschaft: ZONE_DEFAULTS.herrschaft.target };
  try {
    const s = JSON.parse(localStorage.getItem('aw-zone') || 'null');
    if (s && MODES.some(m => m[0] === s.mode)) mode = s.mode;
    if (s && s.targets) for (const k in targets) if (s.targets[k] > 0) targets[k] = +s.targets[k];
  } catch (e) { }
  const save = () => { try { localStorage.setItem('aw-zone', JSON.stringify({ mode, targets })); } catch (e) { } };

  /* ---- setup panel: "Siegbedingung" right below the terrain choice ---- */
  const box = document.createElement('div');
  box.className = 'zone-box';
  box.innerHTML = `<div class="h">Siegbedingung</div>
    <div class="seg" role="group" aria-label="Siegbedingung">${MODES.map(m => `<button data-zone-mode="${m[0]}" aria-pressed="false">${m[1]}</button>`).join('')}</div>
    <div class="zone-opts">
      <div class="qrow"><label for="zoneTarget">Ziel</label><div class="row"><input type="range" id="zoneTarget"><output id="zoneTargetO"></output></div></div>
      <div class="row genrow"><button class="ghost" id="zonePlace" aria-pressed="false">Zonen setzen</button><button class="ghost" id="zoneAuto">Automatisch</button></div>
    </div>
    <p class="sdesc" id="zoneDesc"></p>`;
  const anchor = document.querySelector('#terrain')?.closest('#panel > div');
  if (anchor) anchor.after(box); else document.querySelector('#panel')?.appendChild(box);
  const q = s => box.querySelector(s);
  const range = q('#zoneTarget'), out = q('#zoneTargetO'), placeBtn = q('#zonePlace'), autoBtn = q('#zoneAuto');

  function syncUI() {
    box.querySelectorAll('[data-zone-mode]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.zoneMode === mode)));
    const on = mode !== 'vernichtung';
    q('.zone-opts').hidden = !on;
    let desc = MODES.find(m => m[0] === mode)[2];
    if (on) {
      const [a, b, s] = TARGET_RANGE[mode];
      range.min = a; range.max = b; range.step = s; range.value = targets[mode];
      out.textContent = fmt(targets[mode]) + ' P.';
      desc += ' Vernichtung gewinnt weiterhin. Klick setzt eine Zone' + (mode === 'koenig' ? ' (versetzt den Hügel)' : '') + ', Shift-Klick entfernt.';
    }
    q('#zoneDesc').textContent = desc;
    placeBtn.setAttribute('aria-pressed', String(placing));
    autoBtn.disabled = !custom;
  }
  function setPlacing(on) {
    placing = on;
    const bp = document.querySelector('#bPlace');
    if (on && bp && bp.getAttribute('aria-pressed') === 'true') bp.click(); // one placement tool at a time
    syncUI();
  }
  box.querySelectorAll('[data-zone-mode]').forEach(b => b.addEventListener('click', () => {
    if (b.dataset.zoneMode === mode) return;
    mode = b.dataset.zoneMode; custom = null; placing = false; save(); syncUI(); resetup();
  }));
  range.addEventListener('input', () => { targets[mode] = +range.value; out.textContent = fmt(targets[mode]) + ' P.'; });
  range.addEventListener('change', () => { save(); resetup(); });
  placeBtn.addEventListener('click', () => setPlacing(!placing));
  autoBtn.addEventListener('click', () => { custom = null; syncUI(); resetup(); });
  document.querySelector('#bPlace')?.addEventListener('click', e => { if (e.currentTarget.getAttribute('aria-pressed') === 'true' && placing) setPlacing(false); });
  syncUI();

  /* ---- HUD: points strip under the score ---- */
  const hud = document.createElement('div');
  hud.className = 'zone-hud'; hud.hidden = true; hud.setAttribute('aria-live', 'off');
  hud.innerHTML = '<b class="zone-s0">0</b><div class="zone-track" title="Punkte bis zum Sieg"><i class="zone-f0"></i><i class="zone-f1"></i></div><b class="zone-s1">0</b><span class="zone-pips"></span><span class="zone-goal"></span>';
  document.body.appendChild(hud);
  const hq = s => hud.querySelector(s);

  /* ---- 3D: ring, fill, progress arc, mast and voxel flag per zone ---- */
  const root = new THREE.Group(); scene.add(root);
  const boxGeo = new THREE.BoxGeometry(1, 1, 1), voxGeo = new THREE.BoxGeometry(VS, VS, VS * 0.6);
  const mastMat = new THREE.MeshToonMaterial({ color: 0x8a6a48, gradientMap: toonGrad });
  const stoneMat = new THREE.MeshToonMaterial({ color: 0x9a9890, gradientMap: toonGrad });
  const knobMat = new THREE.MeshToonMaterial({ color: 0xffc93c, gradientMap: toonGrad });
  const flagMat = new THREE.MeshToonMaterial({ color: 0xffffff, gradientMap: toonGrad });
  let vis = [], visKey = '', visTerr = null;
  const ghost = new THREE.Mesh(new THREE.RingGeometry(0.9, 1, 64), new THREE.MeshBasicMaterial({ color: CONTEST, transparent: true, opacity: 0.7, depthWrite: false, side: THREE.DoubleSide }));
  ghost.rotation.x = -Math.PI / 2; ghost.visible = false; ghost.renderOrder = 2; scene.add(ghost);
  const gHit = new THREE.Vector3(), ptr = { x: 0, y: 0, inside: false };
  const canvas = document.querySelector('#c');
  canvas?.addEventListener('pointermove', e => { ptr.x = e.clientX; ptr.y = e.clientY; ptr.inside = true; });
  canvas?.addEventListener('pointerleave', () => { ptr.inside = false; });

  // band between radii r0 and r1 around (x, z), every vertex on the ground; local x/z, world y; arc starts at -z
  function ringGeo(x, z, r0, r1, segs, lift) {
    const pos = new Float32Array((segs + 1) * 6), idx = [];
    for (let s = 0; s <= segs; s++) {
      const a = -Math.PI / 2 + s / segs * Math.PI * 2, c = Math.cos(a), si = Math.sin(a);
      for (let j = 0; j < 2; j++) {
        const rr = j ? r1 : r0, px = x + c * rr, pz = z + si * rr, p = (s * 2 + j) * 3;
        pos[p] = px - x; pos[p + 1] = groundH(px, pz) + lift; pos[p + 2] = pz - z;
      }
      if (s < segs) { const b = s * 2; idx.push(b, b + 1, b + 2, b + 1, b + 3, b + 2); }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3)); g.setIndex(idx);
    return g;
  }
  function discGeo(x, z, r, segs, bands, lift) {
    const gs = []; for (let b = 0; b < bands; b++) gs.push(ringGeo(x, z, r * b / bands, r * (b + 1) / bands, segs, lift));
    const n = gs[0].attributes.position.count, pos = new Float32Array(n * 3 * bands), idx = [];
    gs.forEach((g, b) => { pos.set(g.attributes.position.array, b * n * 3); for (const i of g.index.array) idx.push(i + b * n); g.dispose(); });
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3)); g.setIndex(idx);
    return g;
  }
  const basic = (op) => new THREE.MeshBasicMaterial({ color: NEUTRAL, transparent: true, opacity: op, depthWrite: false, side: THREE.DoubleSide, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
  function buildVis(m) {
    clearVis();
    for (let k = 0; k < m.n; k++) {
      const x = m.x[k], z = m.z[k], r = m.r[k], segs = Math.max(48, Math.round(r * 6)), y0 = groundH(x, z);
      const g = new THREE.Group(); g.position.set(x, 0, z);
      const fill = new THREE.Mesh(discGeo(x, z, r, segs >> 1, 4, 0.06), basic(0.12));
      const ring = new THREE.Mesh(ringGeo(x, z, r - 0.45, r, segs, 0.1), basic(0.9));
      const arc = new THREE.Mesh(ringGeo(x, z, r - 1.15, r - 0.6, segs, 0.1), basic(0.85));
      fill.renderOrder = 1; ring.renderOrder = 2; arc.renderOrder = 2;
      const base = new THREE.Mesh(boxGeo, stoneMat); base.scale.set(1.3, 0.5, 1.3); base.position.y = y0 + 0.25;
      const mast = new THREE.Mesh(boxGeo, mastMat); mast.scale.set(0.26, MAST_H, 0.26); mast.position.y = y0 + MAST_H / 2;
      const knob = new THREE.Mesh(boxGeo, knobMat); knob.scale.setScalar(0.5); knob.position.y = y0 + MAST_H + 0.2;
      const flag = new THREE.InstancedMesh(voxGeo, flagMat, FW * FH);
      flag.instanceMatrix.setUsage(THREE.DynamicDrawUsage); flag.frustumCulled = false;
      for (const mm of [base, mast, knob, flag]) mm.castShadow = true;
      g.add(fill, ring, arc, base, mast, knob, flag); root.add(g);
      vis.push({ g, fill, ring, arc, flag, y0, segs, col: new THREE.Color(), tmp: new THREE.Color() });
    }
  }
  function clearVis() {
    for (const v of vis) {
      root.remove(v.g);
      for (const m of [v.fill, v.ring, v.arc]) { m.geometry.dispose(); m.material.dispose(); }
      v.flag.dispose();
    }
    vis = [];
  }
  const M4 = new THREE.Matrix4(), Q0 = new THREE.Quaternion(), V0 = new THREE.Vector3(), S1 = new THREE.Vector3(1, 1, 1);
  const zoneName = k => last && last.mode === 'koenig' && last.n === 1 ? 'den Hügel' : 'Zone ' + LETTERS[k];

  // worker message { type: 'zone' } (forwarded by main.js, also from a clip's worker)
  function message(m) {
    if (!m || m.type !== 'zone' || m.gen !== gen()) return;
    if (!m.n) { last = null; prevO = []; dp = []; if (vis.length) clearVis(); visKey = ''; return; }
    const key = m.x.map((x, k) => `${x.toFixed(2)},${m.z[k].toFixed(2)},${m.r[k]}`).join(';'), T = terrain();
    if (key !== visKey || T !== visTerr || vis.length !== m.n) { visKey = key; visTerr = T; buildVis(m); dp = m.p.slice(); prevO = m.o.slice(); }
    if (simState() === 'running') for (let k = 0; k < m.n; k++) {
      const ow = m.o[k];
      if (ow >= 0 && ow !== prevO[k]) toast(`${TEAMS[ow].name} hat ${zoneName(k)} eingenommen`);
    }
    prevO = m.o.slice();
    last = m;
  }

  function frame(now) {
    const dt = Math.min(0.1, tPrev ? (now - tPrev) / 1000 : 0); tPrev = now;
    const setup = simState() === 'setup';
    // placement preview
    ghost.visible = false;
    if (placing && setup && mode !== 'vernichtung' && ptr.inside && groundAt(ptr.x, ptr.y, gHit)) {
      ghost.visible = true; ghost.position.set(gHit.x, gHit.y + 0.12, gHit.z); ghost.scale.setScalar(ZONE_DEFAULTS[mode].r);
    }
    const m = last;
    if (!m || !vis.length) { hud.hidden = true; return; }
    const t = now / 1000, blink = 0.5 + 0.5 * Math.sin(t * 9);
    for (let k = 0; k < vis.length; k++) {
      const v = vis[k], p = m.p[k], ow = m.o[k], cont = m.cont[k];
      dp[k] += (p - dp[k]) * Math.min(1, dt * 8);
      const a = Math.abs(dp[k]), side = dp[k] < 0 ? 0 : 1;
      const own = ow >= 0 ? TEAMS[ow].main : NEUTRAL;
      v.ring.material.color.setHex(cont ? (blink > 0.5 ? CONTEST : own) : own);
      v.fill.material.color.setHex(own); v.fill.material.opacity = cont ? 0.12 + 0.1 * blink : ow >= 0 ? 0.16 : 0.1;
      v.arc.material.color.setHex(TEAMS[side].light);
      v.arc.geometry.setDrawRange(0, 6 * Math.round(v.segs * a));
      // flag: rises with |progress|, colour of the leading side (paler near neutral), waves in the wind
      v.col.setHex(NEUTRAL).lerp(v.tmp.setHex(TEAMS[side].main), a < 0.02 ? 0 : 0.35 + 0.65 * a);
      const fy = v.y0 + 1.1 + a * (MAST_H - 1.3 - FH * VS);
      for (let j = 0; j < FH; j++) for (let i = 0; i < FW; i++) {
        const n = j * FW + i, w = (i + 1) / FW;
        V0.set(0.2 + (i + 0.5) * VS, fy + (j + 0.5) * VS - w * 0.12, Math.sin(t * 4.2 - i * 0.9 + k) * 0.16 * w);
        v.flag.setMatrixAt(n, M4.compose(V0, Q0, S1));
        v.flag.setColorAt(n, v.tmp.copy(v.col).multiplyScalar((i + j) & 1 ? 0.86 : 1));
      }
      v.flag.instanceMatrix.needsUpdate = true; if (v.flag.instanceColor) v.flag.instanceColor.needsUpdate = true;
    }
    // HUD strip (battle only)
    hud.hidden = setup;
    if (setup || now - hudT < 120) return;
    hudT = now;
    const tg = m.target || 1;
    hq('.zone-s0').textContent = fmt(m.score[0]); hq('.zone-s1').textContent = fmt(m.score[1]);
    hq('.zone-f0').style.width = (50 * Math.min(1, m.score[0] / tg)) + '%'; hq('.zone-f1').style.width = (50 * Math.min(1, m.score[1] / tg)) + '%';
    hq('.zone-goal').textContent = 'Ziel ' + fmt(tg);
    const pips = hq('.zone-pips');
    if (pips.children.length !== m.n) pips.innerHTML = m.x.map((_, k) => `<i class="zone-pip">${m.mode === 'koenig' && m.n === 1 ? '▲' : LETTERS[k]}</i>`).join('');
    for (let k = 0; k < m.n; k++) {
      const el = pips.children[k], a = Math.abs(m.p[k]), side = m.p[k] < 0 ? 0 : 1;
      el.style.setProperty('--zp', a.toFixed(3)); el.style.setProperty('--zc', a > 0.001 ? TEAMS[side].css : 'transparent');
      el.dataset.o = String(m.o[k]); el.classList.toggle('zone-cont', !!m.cont[k]);
    }
  }

  function place(cx, cy, remove) {
    if (!groundAt(cx, cy, gHit)) return;
    const T = terrain();
    if (!remove && T && !isWalkable(T, gHit.x, gHit.z)) { toast('Zonen gehören nicht ins Wasser.'); return; }
    const list = custom ? custom.slice() : last ? last.x.map((x, k) => ({ x, z: last.z[k], r: last.r[k] })) : [];
    let near = -1, nd = 1e18;
    list.forEach((zn, k) => { const d = Math.hypot(zn.x - gHit.x, zn.z - gHit.z); if (d < nd) { nd = d; near = k; } });
    if (remove) {
      if (near < 0 || nd > list[near].r + 3) return;
      if (list.length <= 1) { toast('Mindestens eine Zone muss bleiben.'); return; }
      list.splice(near, 1);
    } else {
      const p = { x: Math.round(gHit.x * 2) / 2, z: Math.round(gHit.z * 2) / 2, r: ZONE_DEFAULTS[mode].r };
      if (list.length >= (mode === 'koenig' ? 1 : ZONE_MAX)) list[near] = p; else list.push(p);
    }
    custom = list; syncUI(); resetup();
  }

  return {
    /** `zones` field for the worker's setup message. */
    msg() {
      if (mode === 'vernichtung') return { mode };
      return custom ? { mode, target: targets[mode], zones: custom.map(z => ({ ...z })) } : { mode, target: targets[mode] };
    },
    /** canvas pointerdown in the setup: place / remove a zone; true = handled */
    pointer(e) {
      if (!placing || mode === 'vernichtung' || simState() !== 'setup' || e.button !== 0 || !e.isPrimary) return false;
      place(e.clientX, e.clientY, e.shiftKey);
      return true;
    },
    frame, message,
    /** Banner subtitle when the battle was won by points (null otherwise). */
    victoryText(w) {
      const m = last;
      if (!m || !m.n || m.won === -2) return null;
      if (w < 0) return `Gleichstand: beide Teams erreichen ${fmt(m.target)} Punkte.`;
      return `${TEAMS[w].name} ${m.mode === 'koenig' ? 'hält den Hügel und erreicht' : 'erreicht'} ${fmt(m.target)} Punkte zuerst.`;
    },
  };
}
